// Screen recording and click capture for both apps (consultant FIN-1150, client portal FIN-1149):
// every text is masked unless it sits in UI chrome, and digits are masked everywhere, so client
// names, amounts and personnummer never reach a recording. See
// specs/consultant-session-replay/requirements.md (CSR-D1) and specs/client-session-replay/requirements.md.
// Typed structurally, so this package needs no posthog-js dependency; both shapes are assignable
// to posthog-js's SessionRecordingOptions and AutocaptureConfig.

/** UI chrome whose text stays readable in a recording. `data-ph-unmask` adds a reviewed element. */
const READABLE = 'nav, button, label, th, [role="tab"], [role="menuitem"], [data-ph-unmask]'
/** Wins over READABLE: a button or menu that shows client data (the company picker) is marked with it. */
const ALWAYS_MASKED = '[data-ph-mask]'

/** Shown as a block, never as content: document and receipt previews are images, PDFs or canvases. */
const BLOCKED = ['img', 'iframe', 'embed', 'object', 'canvas', 'video', '[data-ph-block]'].join(', ')

export function maskRecordedText(text: string, element: HTMLElement | undefined): string {
  const readable = element?.closest(READABLE) != null && element.closest(ALWAYS_MASKED) == null
  return readable ? text.replace(/\d/g, '*') : text.replace(/\S/g, '*')
}

export const MASKED_SESSION_RECORDING = {
  maskAllInputs: true,
  maskTextSelector: '*',
  maskTextFn: maskRecordedText,
  blockSelector: BLOCKED,
  recordHeaders: false,
  recordBody: false,
  recordCrossOriginIframes: false,
} as const

// Element text and attributes can carry a company name or an amount, so autocapture masks text and
// drops the attributes that hold free text; tag, classes, ids and data-* still say which control it was.
export const MASKED_AUTOCAPTURE = {
  dom_event_allowlist: ['click', 'submit'] as Array<'click' | 'submit'>,
  capture_copied_text: false,
  element_attribute_ignorelist: ['aria-label', 'title', 'value', 'placeholder', 'alt'],
}

/**
 * PostHog init options both apps share: nothing stored on the device (identity lives in memory and
 * is gone on reload), recording and click capture off until the app identifies the signed-in user,
 * every recording masked, no console logs or network timing.
 */
export const MEMORY_ONLY_ANALYTICS = {
  api_host: '/ingest',
  ui_host: 'https://eu.posthog.com',
  persistence: 'memory' as const,
  person_profiles: 'identified_only' as const,
  disable_session_recording: true,
  autocapture: false,
  mask_all_text: true,
  session_recording: MASKED_SESSION_RECORDING,
  enable_recording_console_log: false,
  capture_performance: { network_timing: false },
  capture_pageview: 'history_change' as const,
}

export type AnalyticsProbe = () => {
  distinctId: string
  companyId: string | null
  autocapture: boolean
  recording: boolean
  /** Client portal only (FIN-1199): the active company turned sharing off, so nothing is sent. */
  blocked?: boolean
}

declare global {
  interface Window {
    /** Spectest builds only: who PostHog thinks the user is, read by end-to-end tests. */
    __analyticsProbe?: AnalyticsProbe
  }
}

interface ProbedPostHog {
  get_distinct_id(): string
  get_property(name: string): unknown
  config: { autocapture?: unknown; disable_session_recording?: boolean }
}

export function analyticsProbe(ph: ProbedPostHog): AnalyticsProbe {
  return () => ({
    distinctId: ph.get_distinct_id(),
    companyId: (ph.get_property('company_id') as string | undefined) ?? null,
    autocapture: Boolean(ph.config.autocapture),
    // Asked for, not proven: the recorder script loads from /ingest, which is closed in Spectest.
    recording: ph.config.disable_session_recording === false,
  })
}
